import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';

export default function DrywallRepairCostBoiseIdaho() {
  useSEO({
    title: 'Drywall Repair Cost Boise Idaho: 2025 Pricing Guide & Estimates',
    description: 'Complete guide to drywall repair costs in Boise Idaho. Learn pricing for holes, cracks, water damage, and full replacements. Get accurate estimates for your project.',
    keywords: 'drywall repair cost Boise, drywall repair prices Idaho, patch drywall Boise, drywall contractor Treasure Valley, drywall installation cost Boise',
    canonical: `${import.meta.env.VITE_SITE_URL}/blog/drywall-repair-cost-boise-idaho`,
    ogType: 'article',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Drywall Repair Cost Boise Idaho: 2025 Pricing Guide & Estimates',
        description: 'Complete guide to drywall repair costs in Boise Idaho. Learn pricing for holes, cracks, water damage, and full replacements. Get accurate estimates for your project.',
        author: {
          '@type': 'Organization',
          name: 'Fix Right Services'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Fix Right Services',
          logo: {
            '@type': 'ImageObject',
            url: `${import.meta.env.VITE_SITE_URL}/images/logo.svg`
          }
        },
        datePublished: '2025-01-15',
        dateModified: '2025-01-15'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: import.meta.env.VITE_SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: `${import.meta.env.VITE_SITE_URL}/blog`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Drywall Repair Cost Boise Idaho',
            item: `${import.meta.env.VITE_SITE_URL}/blog/drywall-repair-cost-boise-idaho`
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How much does drywall repair cost in Boise Idaho?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Small hole repairs (under 6 inches) cost $75-$150, medium holes (6-12 inches) cost $150-$300, large holes (12+ inches) cost $250-$500, and full room drywall replacement costs $1.50-$3.50 per square foot. Handyman hourly rates are $50-$85, while specialized drywall contractors charge $60-$100 per hour.'
            }
          },
          {
            '@type': 'Question',
            name: 'What factors affect drywall repair costs in Treasure Valley?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Key factors include damage size and complexity, ceiling vs wall repairs (ceilings cost 20-30% more), texture matching requirements, number of repairs, accessibility, water damage extent, and whether painting is included. Emergency repairs cost 50-100% more than scheduled work.'
            }
          },
          {
            '@type': 'Question',
            name: 'Should I hire a handyman or drywall contractor in Boise?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Hire handymen for small repairs (1-3 holes), minor cracks, and projects under $500. Hire specialized drywall contractors for extensive damage, full room replacements, complex texture matching, water damage repairs, and projects over $1,000. Handymen offer better value for simple repairs.'
            }
          },
          {
            '@type': 'Question',
            name: 'How long does drywall repair take in Boise?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Small hole repairs take 1-2 hours plus drying time (24-48 hours between coats). Medium repairs take 2-4 hours plus drying. Large repairs or full room replacement take 1-3 days. Total project time including painting is typically 2-5 days depending on scope.'
            }
          },
          {
            '@type': 'Question',
            name: 'Does drywall repair cost include painting in Boise?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Basic quotes usually include primer and touch-up paint matching the repair area. Full room painting costs extra: $200-$400 for standard rooms. Always clarify what\'s included in estimates. Some contractors offer package deals combining repair and painting at discounted rates.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can I DIY drywall repair or should I hire a professional in Boise?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Small nail holes and hairline cracks are DIY-friendly with spackle and paint. Holes larger than 2 inches, ceiling repairs, water damage, and texture matching require professional skills. Poor DIY repairs are obvious and reduce home value. Professionals ensure invisible, lasting repairs.'
            }
          }
        ]
      }
    ]
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src="/images/logo.svg" alt="Fix Right Services Logo" className="h-8 w-auto" />
              <span className="text-xl font-bold text-gray-900">Fix Right Services</span>
            </Link>
            <div className="flex items-center space-x-8">
              <Link to="/blog" className="text-gray-700 hover:text-teal-600 font-medium transition-colors whitespace-nowrap">Blog</Link>
              <a href="/#contact" className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap">Get Free Quote</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
            <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/blog" className="hover:text-teal-600 transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900">Drywall Repair Cost Boise Idaho</span>
          </nav>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Drywall Repair Cost Boise Idaho: 2025 Pricing Guide &amp; Estimates
          </h1>
          
          <div className="flex items-center space-x-4 text-gray-600 mb-8">
            <time dateTime="2025-01-15">January 15, 2025</time>
            <span>•</span>
            <span>10 min read</span>
            <span>•</span>
            <span className="text-teal-600 font-medium">Handyman Services</span>
          </div>

          <img 
            src="https://public.readdy.ai/ai/img_res/9d630af0f3012cc425cc47d7cd0e2e18.jpg"
            alt="Drywall repair cost guide for Boise Idaho homeowners"
            title="Drywall Repair Cost Boise Idaho - Fix Right Services"
            className="w-full h-96 object-cover object-top rounded-xl mb-8"
          />
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Understanding <strong>drywall repair costs in Boise Idaho</strong> helps you budget accurately and avoid overpaying for common repairs. Whether you're dealing with small nail holes, large damage from accidents, or water-damaged walls, this comprehensive guide provides real <strong>Treasure Valley</strong> pricing, cost factors, and tips for choosing between DIY and professional repairs. Get the information you need to make informed decisions about your drywall projects.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="average-costs" href="#average-costs" className="hover:text-teal-600 transition-colors">
              Average Drywall Repair Costs in Boise
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Drywall repair costs in Boise</strong> vary significantly based on damage size, location, and complexity. Here's what <strong>Treasure Valley homeowners</strong> can expect to pay in 2025:
          </p>

          <div className="bg-gray-50 rounded-xl p-8 mb-8">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-3 px-4 font-bold text-gray-900">Repair Type</th>
                  <th className="text-left py-3 px-4 font-bold text-gray-900">Size/Scope</th>
                  <th className="text-left py-3 px-4 font-bold text-gray-900">Cost Range</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4">Small Hole Repair</td>
                  <td className="py-3 px-4">Under 6 inches</td>
                  <td className="py-3 px-4 font-semibold text-teal-600">$75-$150</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4">Medium Hole Repair</td>
                  <td className="py-3 px-4">6-12 inches</td>
                  <td className="py-3 px-4 font-semibold text-teal-600">$150-$300</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4">Large Hole Repair</td>
                  <td className="py-3 px-4">12+ inches</td>
                  <td className="py-3 px-4 font-semibold text-teal-600">$250-$500</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4">Crack Repair</td>
                  <td className="py-3 px-4">Per crack</td>
                  <td className="py-3 px-4 font-semibold text-teal-600">$50-$150</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4">Ceiling Repair</td>
                  <td className="py-3 px-4">Small to medium</td>
                  <td className="py-3 px-4 font-semibold text-teal-600">$200-$500</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4">Water Damage Repair</td>
                  <td className="py-3 px-4">Per affected area</td>
                  <td className="py-3 px-4 font-semibold text-teal-600">$300-$1,200</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4">Texture Matching</td>
                  <td className="py-3 px-4">Per repair</td>
                  <td className="py-3 px-4 font-semibold text-teal-600">$50-$200 extra</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4">Full Room Replacement</td>
                  <td className="py-3 px-4">Per sq ft</td>
                  <td className="py-3 px-4 font-semibold text-teal-600">$1.50-$3.50</td>
                </tr>
                <tr>
                  <td className="py-3 px-4">Hourly Rate (Handyman)</td>
                  <td className="py-3 px-4">Labor only</td>
                  <td className="py-3 px-4 font-semibold text-teal-600">$50-$85/hour</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-gray-700 leading-relaxed mb-8">
            These prices include materials, labor, and basic finishing. Painting is typically extra unless specified. Most <strong>Boise handyman services</strong> have minimum charges of $75-$150 for small repairs, making it cost-effective to bundle multiple repairs in one visit.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="cost-factors" href="#cost-factors" className="hover:text-teal-600 transition-colors">
              Factors That Affect Drywall Repair Costs
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Several variables influence the final cost of <strong>drywall repair in Treasure Valley</strong>. Understanding these factors helps you get accurate estimates:
          </p>

          <div className="space-y-6 mb-8">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">1</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Damage Size and Complexity</h4>
                <p className="text-gray-700">Larger holes require more materials and labor. Holes near corners, outlets, or switches add complexity. Irregular damage is harder to repair than clean cuts, increasing costs by 20-40%.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">2</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Location: Wall vs Ceiling</h4>
                <p className="text-gray-700">Ceiling repairs cost 20-30% more than wall repairs due to difficulty, specialized equipment needs, and gravity challenges. Vaulted or high ceilings add another 30-50% to costs.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">3</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Texture Matching</h4>
                <p className="text-gray-700">Smooth walls are easiest to repair. Orange peel, knockdown, and popcorn textures require specialized skills and tools, adding $50-$200 per repair. Perfect texture matching is an art that justifies higher costs.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">4</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Number of Repairs</h4>
                <p className="text-gray-700">Multiple repairs in one visit reduce per-repair costs by 20-30%. Contractors offer volume discounts since setup time is shared across repairs. Bundle repairs for better value.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">5</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Accessibility</h4>
                <p className="text-gray-700">Repairs behind furniture, in tight spaces, or requiring furniture moving cost more. Contractors may charge $50-$150 extra for moving heavy items or working in cramped areas.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">6</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Water Damage Extent</h4>
                <p className="text-gray-700">Water-damaged drywall requires complete replacement, not patching. Mold remediation, insulation replacement, and structural drying add $500-$2,000+ to basic repair costs.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">7</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Painting Inclusion</h4>
                <p className="text-gray-700">Basic quotes include primer and touch-up paint. Full room painting costs extra: $200-$400 for standard rooms. Clarify what's included to avoid surprise charges.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">8</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Emergency vs Scheduled Work</h4>
                <p className="text-gray-700">Emergency repairs (same-day or weekend) cost 50-100% more than scheduled work. Plan ahead when possible to avoid premium rates.</p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="repair-types" href="#repair-types" className="hover:text-teal-600 transition-colors">
              Common Drywall Repair Types and Costs
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Different damage types require specific repair approaches. Here's a detailed breakdown of common <strong>drywall repairs in Boise</strong>:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Small Hole Repairs (Nail Holes, Screws, Small Dents)</h3>
          <div className="bg-green-50 rounded-xl p-6 mb-6">
            <p className="text-gray-700 mb-3"><strong>Cost:</strong> $75-$150 for 1-5 holes</p>
            <p className="text-gray-700 mb-3"><strong>Process:</strong> Fill with spackle or joint compound, sand smooth, prime, and paint. Takes 1-2 hours plus drying time.</p>
            <p className="text-gray-700 mb-3"><strong>Best for:</strong> Picture hanging holes, doorknob dents, small anchor holes</p>
            <p className="text-gray-700"><strong>DIY potential:</strong> High—this is the most DIY-friendly repair with minimal tools needed</p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Medium Hole Repairs (6-12 inches)</h3>
          <div className="bg-blue-50 rounded-xl p-6 mb-6">
            <p className="text-gray-700 mb-3"><strong>Cost:</strong> $150-$300 per hole</p>
            <p className="text-gray-700 mb-3"><strong>Process:</strong> Cut clean edges, install backing support, attach drywall patch, tape and mud seams, sand, texture match, prime, and paint. Takes 2-4 hours plus drying.</p>
            <p className="text-gray-700 mb-3"><strong>Best for:</strong> Accidental impacts, removed fixtures, cable/plumbing access holes</p>
            <p className="text-gray-700"><strong>DIY potential:</strong> Moderate—requires more skill for invisible repairs, especially texture matching</p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Large Hole Repairs (12+ inches)</h3>
          <div className="bg-amber-50 rounded-xl p-6 mb-6">
            <p className="text-gray-700 mb-3"><strong>Cost:</strong> $250-$500 per hole</p>
            <p className="text-gray-700 mb-3"><strong>Process:</strong> Cut to nearest studs, install new drywall section, secure to framing, tape all seams, apply multiple mud coats, sand smooth, texture match, prime, and paint. Takes 4-8 hours plus drying.</p>
            <p className="text-gray-700 mb-3"><strong>Best for:</strong> Major impacts, removed cabinets, structural modifications</p>
            <p className="text-gray-700"><strong>DIY potential:</strong> Low—professional results require experience with taping, mudding, and texture matching</p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Crack Repairs</h3>
          <div className="bg-purple-50 rounded-xl p-6 mb-6">
            <p className="text-gray-700 mb-3"><strong>Cost:</strong> $50-$150 per crack</p>
            <p className="text-gray-700 mb-3"><strong>Process:</strong> Widen crack slightly, fill with setting compound, apply mesh tape, mud over tape, sand, prime, and paint. Structural cracks may require additional reinforcement.</p>
            <p className="text-gray-700 mb-3"><strong>Best for:</strong> Settlement cracks, corner cracks, seam separation</p>
            <p className="text-gray-700"><strong>DIY potential:</strong> Moderate—simple cracks are DIY-friendly, but recurring cracks indicate structural issues needing professional assessment</p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Water Damage Repairs</h3>
          <div className="bg-red-50 rounded-xl p-6 mb-6">
            <p className="text-gray-700 mb-3"><strong>Cost:</strong> $300-$1,200+ depending on extent</p>
            <p className="text-gray-700 mb-3"><strong>Process:</strong> Identify and fix water source, dry affected area completely, remove damaged drywall and insulation, treat for mold if present, install new drywall, finish, and paint. May take 3-7 days including drying time.</p>
            <p className="text-gray-700 mb-3"><strong>Best for:</strong> Roof leaks, plumbing failures, flooding damage</p>
            <p className="text-gray-700"><strong>DIY potential:</strong> Very low—requires moisture detection, mold assessment, and proper drying to prevent future problems</p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Ceiling Repairs</h3>
          <div className="bg-cyan-50 rounded-xl p-6 mb-8">
            <p className="text-gray-700 mb-3"><strong>Cost:</strong> $200-$500 for small to medium repairs</p>
            <p className="text-gray-700 mb-3"><strong>Process:</strong> Similar to wall repairs but requires scaffolding or stilts, overhead work challenges, and careful texture matching. Gravity makes mudding more difficult.</p>
            <p className="text-gray-700 mb-3"><strong>Best for:</strong> Ceiling holes, water stains, popcorn texture removal areas</p>
            <p className="text-gray-700"><strong>DIY potential:</strong> Low—overhead work is physically demanding and requires specialized equipment for quality results</p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="handyman-vs-contractor" href="#handyman-vs-contractor" className="hover:text-teal-600 transition-colors">
              Handyman vs Drywall Contractor: Which to Hire?
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Choosing between a <strong>handyman and specialized drywall contractor</strong> affects both cost and quality. Here's how to decide:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-teal-50 rounded-xl p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-tools-line text-teal-600 text-2xl mr-3"></i>
                Hire a Handyman When:
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                  <span>Repairing 1-3 small to medium holes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                  <span>Fixing minor cracks and dents</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                  <span>Total project cost under $500</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                  <span>Simple texture matching (smooth or light orange peel)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                  <span>Bundling with other handyman tasks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-1"></i>
                  <span>Quick turnaround needed</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 text-sm"><strong>Typical rate:</strong> $50-$85/hour in Boise</p>
            </div>

            <div className="bg-red-50 rounded-xl p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-user-star-line text-red-600 text-2xl mr-3"></i>
                Hire a Drywall Contractor When:
              </h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                  <span>Extensive damage (multiple large holes)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                  <span>Full room or multi-room replacement</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                  <span>Complex texture matching (knockdown, skip trowel)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                  <span>Water damage with mold concerns</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                  <span>Projects over $1,000</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-fill text-red-600 mr-2 mt-1"></i>
                  <span>Commercial or rental property work</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 text-sm"><strong>Typical rate:</strong> $60-$100/hour in Boise</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="diy-vs-professional" href="#diy-vs-professional" className="hover:text-teal-600 transition-colors">
              DIY vs Professional Drywall Repair
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Many <strong>Boise homeowners</strong> consider DIY drywall repair to save money. Here's an honest assessment of when DIY makes sense:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">DIY-Friendly Repairs</h3>
          <div className="bg-green-50 rounded-xl p-6 mb-6">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <div>
                  <strong>Nail holes and small dents:</strong> Spackle, sand, paint. Total cost: $10-$20 in materials. Time: 30 minutes plus drying.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <div>
                  <strong>Hairline cracks:</strong> Mesh tape and joint compound. Cost: $15-$30. Time: 1 hour plus drying.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                <div>
                  <strong>Small holes (under 2 inches):</strong> Patch kit from hardware store. Cost: $10-$25. Time: 1-2 hours plus drying.
                </div>
              </li>
            </ul>
            <p className="text-gray-700 mt-4"><strong>Savings:</strong> $50-$100 per repair compared to professional rates</p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Hire a Professional For:</h3>
          <div className="bg-red-50 rounded-xl p-6 mb-8">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-close-line text-red-600 text-xl mr-3 mt-1"></i>
                <div>
                  <strong>Holes larger than 2 inches:</strong> Requires proper backing, taping, and mudding skills. Poor DIY repairs are obvious and reduce home value.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-close-line text-red-600 text-xl mr-3 mt-1"></i>
                <div>
                  <strong>Ceiling repairs:</strong> Overhead work is physically demanding and requires specialized equipment. Mistakes are highly visible.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-close-line text-red-600 text-xl mr-3 mt-1"></i>
                <div>
                  <strong>Texture matching:</strong> Achieving invisible repairs with knockdown or orange peel texture requires experience and proper tools.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-close-line text-red-600 text-xl mr-3 mt-1"></i>
                <div>
                  <strong>Water damage:</strong> Requires moisture detection, mold assessment, and proper drying. DIY risks health hazards and future problems.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-close-line text-red-600 text-xl mr-3 mt-1"></i>
                <div>
                  <strong>Multiple repairs:</strong> Professionals work faster and achieve consistent results across multiple repairs. Time savings justify the cost.
                </div>
              </li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="cost-saving-tips" href="#cost-saving-tips" className="hover:text-teal-600 transition-colors">
              How to Save Money on Drywall Repairs
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Smart <strong>Treasure Valley homeowners</strong> use these strategies to reduce drywall repair costs without sacrificing quality:
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">1</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Bundle Multiple Repairs</h4>
                <p className="text-gray-700">Schedule all repairs in one visit to share setup and travel costs. Most contractors offer 20-30% discounts for multiple repairs. Wait until you have 3-5 repairs before calling.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">2</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Get Multiple Quotes</h4>
                <p className="text-gray-700">Obtain 2-3 estimates from different <strong>Boise handyman services</strong> or contractors. Prices can vary 30-50% for identical work. Ensure quotes include the same scope for accurate comparison.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">3</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Do Your Own Prep and Cleanup</h4>
                <p className="text-gray-700">Move furniture yourself, protect floors with drop cloths, and handle cleanup after repairs. This can save $50-$150 per visit in labor charges.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">4</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Skip Painting if Possible</h4>
                <p className="text-gray-700">If you're comfortable painting, have the contractor stop after priming. You can paint yourself and save $100-$200. Ensure you get exact paint color match information.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">5</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Schedule During Off-Peak Times</h4>
                <p className="text-gray-700">Book repairs during slower periods (winter, mid-week) for better rates and availability. Avoid emergency rates by planning ahead whenever possible.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">6</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Accept "Good Enough" for Hidden Areas</h4>
                <p className="text-gray-700">For repairs behind furniture or in closets, you don't need perfect texture matching. Communicate this to save on finishing costs.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0 mt-1">7</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">DIY the Simplest Repairs</h4>
                <p className="text-gray-700">Handle nail holes and tiny dents yourself with $10 spackle. Save professional services for repairs requiring skill and experience.</p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="getting-quotes" href="#getting-quotes" className="hover:text-teal-600 transition-colors">
              Getting Accurate Drywall Repair Quotes
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Accurate quotes prevent surprise charges and help you budget properly. Here's how to get reliable estimates from <strong>Boise contractors</strong>:
          </p>

          <div className="bg-teal-50 rounded-xl p-6 mb-8">
            <h4 className="text-xl font-bold text-gray-900 mb-4">Information to Provide</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• Number and size of repairs needed</li>
              <li>• Location (wall vs ceiling, room type)</li>
              <li>• Texture type (smooth, orange peel, knockdown, popcorn)</li>
              <li>• Whether painting is needed</li>
              <li>• Photos of damage (for remote estimates)</li>
              <li>• Timeline and urgency</li>
              <li>• Access considerations (furniture, tight spaces)</li>
            </ul>
          </div>

          <div className="bg-amber-50 rounded-xl p-6 mb-8">
            <h4 className="text-xl font-bold text-gray-900 mb-4">Questions to Ask Contractors</h4>
            <ul className="space-y-2 text-gray-700">
              <li>• Is this a firm quote or estimate?</li>
              <li>• What exactly is included (materials, labor, painting)?</li>
              <li>• How long will the repair take?</li>
              <li>• Do you guarantee texture matching?</li>
              <li>• What's your warranty on repairs?</li>
              <li>• Are you licensed and insured?</li>
              <li>• Can you provide references or photos of similar work?</li>
              <li>• What's your payment schedule?</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="red-flags" href="#red-flags" className="hover:text-teal-600 transition-colors">
              Red Flags When Hiring Drywall Contractors
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Protect yourself from poor work and scams by recognizing these warning signs:
          </p>

          <div className="bg-red-50 rounded-xl p-6 mb-8">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-error-warning-line text-red-600 text-xl mr-3 mt-1"></i>
                <span><strong>Quotes significantly lower than competitors:</strong> Usually indicates cut corners, poor materials, or hidden charges. If it seems too good to be true, it is.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-error-warning-line text-red-600 text-xl mr-3 mt-1"></i>
                <span><strong>Requests full payment upfront:</strong> Standard is 0-30% deposit, balance on completion. Never pay in full before work starts.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-error-warning-line text-red-600 text-xl mr-3 mt-1"></i>
                <span><strong>No written estimate or contract:</strong> Verbal agreements lead to disputes. Always get detailed written quotes.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-error-warning-line text-red-600 text-xl mr-3 mt-1"></i>
                <span><strong>Can't provide insurance proof:</strong> Uninsured contractors put you at risk for liability. Always verify active insurance.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-error-warning-line text-red-600 text-xl mr-3 mt-1"></i>
                <span><strong>Pressure tactics or limited-time offers:</strong> Quality contractors don't need high-pressure sales. Take time to compare options.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-error-warning-line text-red-600 text-xl mr-3 mt-1"></i>
                <span><strong>No local references or portfolio:</strong> Established contractors readily share past work examples and customer contacts.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-error-warning-line text-red-600 text-xl mr-3 mt-1"></i>
                <span><strong>Vague scope descriptions:</strong> "Fix the wall" isn't specific enough. Quotes should detail exact work to be performed.</span>
              </li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="timeline" href="#timeline" className="hover:text-teal-600 transition-colors">
              Drywall Repair Timeline and Process
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Understanding the repair process helps you plan around the work and set realistic expectations:
          </p>

          <div className="space-y-6 mb-8">
            <div className="border-l-4 border-teal-600 bg-gray-50 p-6 rounded-r-xl">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Day 1: Initial Repair</h4>
              <p className="text-gray-700 mb-2">Contractor cuts out damaged area, installs backing and new drywall, applies first coat of joint compound. Time: 1-4 hours depending on size.</p>
              <p className="text-gray-700 text-sm italic">Drying time: 24 hours before next coat</p>
            </div>

            <div className="border-l-4 border-teal-600 bg-gray-50 p-6 rounded-r-xl">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Day 2: Second Coat</h4>
              <p className="text-gray-700 mb-2">Apply second coat of joint compound, feathering edges wider. Time: 30-60 minutes.</p>
              <p className="text-gray-700 text-sm italic">Drying time: 24 hours</p>
            </div>

            <div className="border-l-4 border-teal-600 bg-gray-50 p-6 rounded-r-xl">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Day 3: Final Coat and Sanding</h4>
              <p className="text-gray-700 mb-2">Apply final coat if needed, sand smooth when dry. Time: 1-2 hours.</p>
              <p className="text-gray-700 text-sm italic">Drying time: 24 hours before texture/paint</p>
            </div>

            <div className="border-l-4 border-teal-600 bg-gray-50 p-6 rounded-r-xl">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Day 4: Texture and Prime</h4>
              <p className="text-gray-700 mb-2">Match existing texture, apply primer. Time: 1-2 hours.</p>
              <p className="text-gray-700 text-sm italic">Drying time: 2-4 hours</p>
            </div>

            <div className="border-l-4 border-teal-600 bg-gray-50 p-6 rounded-r-xl">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Day 5: Paint</h4>
              <p className="text-gray-700 mb-2">Apply finish paint (usually 2 coats). Time: 1-2 hours.</p>
              <p className="text-gray-700 text-sm italic">Total project time: 4-5 days for complete repair</p>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed mb-8">
            <strong>Note:</strong> Experienced contractors often batch multiple repairs, working on several at different stages simultaneously to reduce total project time. Small repairs can sometimes be completed in 2-3 days with fast-drying compounds.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="preventing-damage" href="#preventing-damage" className="hover:text-teal-600 transition-colors">
              Preventing Future Drywall Damage
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Prevention is always cheaper than repair. Follow these tips to minimize future <strong>drywall damage in your Boise home</strong>:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-shield-check-line text-teal-600 text-xl mr-2"></i>
                Structural Protection
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Install door stops to prevent doorknob damage</li>
                <li>• Use corner guards in high-traffic areas</li>
                <li>• Add chair rails in dining rooms</li>
                <li>• Secure heavy furniture to walls</li>
                <li>• Use proper anchors for wall-mounted items</li>
              </ul>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-drop-line text-blue-600 text-xl mr-2"></i>
                Moisture Prevention
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Fix plumbing leaks immediately</li>
                <li>• Use bathroom exhaust fans during showers</li>
                <li>• Maintain proper attic ventilation</li>
                <li>• Keep gutters clean and functional</li>
                <li>• Address roof leaks promptly</li>
              </ul>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-hammer-line text-orange-600 text-xl mr-2"></i>
                Proper Installation
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Use stud finders before drilling</li>
                <li>• Choose appropriate anchors for weight</li>
                <li>• Don't over-tighten screws</li>
                <li>• Mark stud locations for future use</li>
                <li>• Hire professionals for heavy installations</li>
              </ul>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-home-heart-line text-red-600 text-xl mr-2"></i>
                General Maintenance
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Address cracks when they first appear</li>
                <li>• Maintain consistent indoor humidity (30-50%)</li>
                <li>• Inspect for foundation settlement issues</li>
                <li>• Touch up paint to prevent moisture infiltration</li>
                <li>• Schedule annual home inspections</li>
              </ul>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="insurance-coverage" href="#insurance-coverage" className="hover:text-teal-600 transition-colors">
              Insurance Coverage for Drywall Repairs
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Understanding when homeowner's insurance covers <strong>drywall repairs</strong> can save thousands. Here's what you need to know:
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2">Usually Covered:</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Sudden water damage (burst pipes, appliance failures)</li>
                <li>• Fire or smoke damage</li>
                <li>• Vandalism or theft-related damage</li>
                <li>• Storm damage (wind, hail)</li>
                <li>• Falling objects</li>
              </ul>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2">Usually NOT Covered:</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Gradual water damage (slow leaks, poor maintenance)</li>
                <li>• Normal wear and tear</li>
                <li>• Accidental damage from residents</li>
                <li>• Foundation settlement cracks</li>
                <li>• Cosmetic damage</li>
                <li>• Damage below deductible amount</li>
              </ul>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed mb-8">
            <strong>Important:</strong> Filing claims for small repairs (under $1,000) often isn't worth it due to deductibles and potential premium increases. Save insurance for major damage exceeding $2,000-$3,000.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            <a id="conclusion" href="#conclusion" className="hover:text-teal-600 transition-colors">
              Making Smart Drywall Repair Decisions
            </a>
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Understanding <strong>drywall repair costs in Boise Idaho</strong> empowers you to make informed decisions about your home maintenance. Small repairs ($75-$150) are often worth professional handling to ensure invisible results, while extensive damage requires specialized contractors for quality outcomes.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            The key to cost-effective drywall repair is bundling multiple repairs in one visit, getting competitive quotes, and knowing when DIY is appropriate versus when professional expertise is essential. Don't let small damage escalate—address issues promptly to prevent more expensive repairs later.
          </p>
          <p className="text-gray-700 leading-relaxed mb-8">
            Whether you need a quick patch or extensive repair, <strong>Treasure Valley</strong> has skilled professionals ready to restore your walls to perfect condition. Invest in quality repairs that maintain your home's value and appearance.
          </p>

          {/* FAQ Section */}
          <div className="bg-gray-50 rounded-xl p-8 my-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">How much does drywall repair cost in Boise Idaho?</h3>
                <p className="text-gray-700 leading-relaxed">Small hole repairs (under 6 inches) cost $75-$150, medium holes (6-12 inches) cost $150-$300, large holes (12+ inches) cost $250-$500, and full room drywall replacement costs $1.50-$3.50 per square foot. Handyman hourly rates are $50-$85, while specialized drywall contractors charge $60-$100 per hour.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">What factors affect drywall repair costs in Treasure Valley?</h3>
                <p className="text-gray-700 leading-relaxed">Key factors include damage size and complexity, ceiling vs wall repairs (ceilings cost 20-30% more), texture matching requirements, number of repairs, accessibility, water damage extent, and whether painting is included. Emergency repairs cost 50-100% more than scheduled work.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Should I hire a handyman or drywall contractor in Boise?</h3>
                <p className="text-gray-700 leading-relaxed">Hire handymen for small repairs (1-3 holes), minor cracks, and projects under $500. Hire specialized drywall contractors for extensive damage, full room replacements, complex texture matching, water damage repairs, and projects over $1,000. Handymen offer better value for simple repairs.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">How long does drywall repair take in Boise?</h3>
                <p className="text-gray-700 leading-relaxed">Small hole repairs take 1-2 hours plus drying time (24-48 hours between coats). Medium repairs take 2-4 hours plus drying. Large repairs or full room replacement take 1-3 days. Total project time including painting is typically 2-5 days depending on scope.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Does drywall repair cost include painting in Boise?</h3>
                <p className="text-gray-700 leading-relaxed">Basic quotes usually include primer and touch-up paint matching the repair area. Full room painting costs extra: $200-$400 for standard rooms. Always clarify what's included in estimates. Some contractors offer package deals combining repair and painting at discounted rates.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Can I DIY drywall repair or should I hire a professional in Boise?</h3>
                <p className="text-gray-700 leading-relaxed">Small nail holes and hairline cracks are DIY-friendly with spackle and paint. Holes larger than 2 inches, ceiling repairs, water damage, and texture matching require professional skills. Poor DIY repairs are obvious and reduce home value. Professionals ensure invisible, lasting repairs.</p>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          <div className="bg-gray-50 rounded-xl p-8 my-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/blog/handyman-services-cost-boise-idaho" className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <i className="ri-money-dollar-circle-line text-teal-600 text-2xl"></i>
                <span className="text-gray-900 font-medium">Handyman Services Cost in Boise</span>
              </Link>
              <Link to="/blog/handyman-vs-contractor-idaho" className="flex items-center space-x-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <i className="ri-user-line text-teal-600 text-2xl"></i>
                <span className="text-gray-900 font-medium">Handyman vs Contractor Idaho</span>
              </Link>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-center text-white my-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Professional Drywall Repair?</h2>
            <p className="text-xl mb-8 text-teal-50">
              Get invisible, lasting repairs from Boise's trusted handyman experts. Free quotes, quality workmanship, and satisfaction guaranteed.
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-50 transition-colors whitespace-nowrap"
            >
              Get Free Estimate
            </a>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-lg mb-4">Fix Right Services</h3>
              <p className="text-gray-400 text-sm">Professional home improvement and handyman services in Boise and Treasure Valley.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="/#services" className="hover:text-white transition-colors">Fence Installation</a></li>
                <li><a href="/#services" className="hover:text-white transition-colors">Handyman Services</a></li>
                <li><a href="/#services" className="hover:text-white transition-colors">Residential Remodeling</a></li>
                <li><a href="/#services" className="hover:text-white transition-colors">Fix &amp; Flip</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><a href="/#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Boise, Idaho</li>
                <li>Treasure Valley Area</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}